/**
 * Catchball routing policy (canon 29 §6).
 *
 * Inheritance: Workflow → Cycle → Track (most-specific wins).
 * Sessions inherit nothing for routing.
 */

export type BallSplitUnit = 'disabled' | 'org_unit' | 'virtual_recipient' | 'track';

export type ResolutionTarget =
  | 'split_node'
  | 'lowest_common_authority'
  | 'cycle_owner'
  | 'originating_requester';

/**
 * Who may rewrite the remaining Path Instance spine while holding the ball.
 * - none: Path frozen after launch
 * - holder_remaining_spine: current ball holder may adjust remaining stops
 * - cycle_owner_only: only cycle owner / process.edit
 */
export type PathAdjustPolicy = 'none' | 'holder_remaining_spine' | 'cycle_owner_only';

/** Workflow.configuration keys (snake_case, persisted). */
export interface CatchballRoutingWorkflowConfig {
  rollout_enabled?: boolean;
  ball_split_unit?: BallSplitUnit;
  ball_split_max_children?: number;
  require_aggregate_before_pass_up?: boolean;
  resolution_target?: ResolutionTarget;
  path_adjust_policy?: PathAdjustPolicy;
}

/** Cycle/Track metadata.catchballRouting (camelCase overrides). */
export interface CatchballRoutingLayerOverride {
  rolloutEnabled?: boolean;
  ballSplitUnit?: BallSplitUnit;
  ballSplitMaxChildren?: number;
  requireAggregateBeforePassUp?: boolean;
  resolutionTarget?: ResolutionTarget;
  pathAdjustPolicy?: PathAdjustPolicy;
}

/** Fully resolved effective routing policy. */
export interface EffectiveCatchballRouting {
  rolloutEnabled: boolean;
  ballSplitUnit: BallSplitUnit;
  ballSplitMaxChildren: number;
  requireAggregateBeforePassUp: boolean;
  resolutionTarget: ResolutionTarget;
  pathAdjustPolicy: PathAdjustPolicy;
  /** True when catchball itself is enabled on the workflow. */
  catchballEnabled: boolean;
}

export const CANONICAL_CATCHBALL_ROUTING: EffectiveCatchballRouting = {
  rolloutEnabled: false,
  ballSplitUnit: 'org_unit',
  ballSplitMaxChildren: 5,
  requireAggregateBeforePassUp: true,
  resolutionTarget: 'split_node',
  pathAdjustPolicy: 'holder_remaining_spine',
  catchballEnabled: true,
};

export type CatchballRoutingSource =
  | 'canonical'
  | 'workflow'
  | 'cycle'
  | 'track';

export interface CatchballRoutingKeyBreakdown {
  key: keyof Omit<EffectiveCatchballRouting, 'catchballEnabled'>;
  value: string | number | boolean;
  source: CatchballRoutingSource;
}

export interface CatchballRoutingInheritanceBreakdown {
  effective: EffectiveCatchballRouting;
  rows: CatchballRoutingKeyBreakdown[];
}
